import { useEffect, useState } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import { useCommercialStructure } from 'commons/customHooks'
import { TranslateComponent, t } from 'commons/libs/i18n'
import {
  dateAdd,
  dateSubtract,
  formatDate,
  getDiffBetweenDates,
  getToday,
} from 'commons/utils/date'
import { capitalize } from 'commons/utils/string'
import {
  ActionTabs,
  BatchSearch,
  DatePicker,
  Flex,
  type HeadItemType,
  type ItemActionTabsType,
  LoadingHavaianas,
  Modal,
  NotFound,
  PaginationV2,
  Select,
  SlimButton,
  Table,
  Text,
} from 'components'
import { Controller, useForm } from 'react-hook-form'
import type { FairShareTrackingPayload } from 'services/fairShare/interfaces'
import {
  fetchFairShareTracking,
  resetTracking,
  setTrackingPage,
  setTrackingPageSize,
} from 'store/FairShareContext/fairShareSlice'
import { useDispatch, useSelector } from 'store/store'

import { DATE_FORMAT, fairShareTrackingModalSchema, notInFuture } from '../validations'
import * as S from './styles'

export type OfficeTrackingModalProps = {
  open: boolean
  setOpen: (open: boolean) => void
}

export const OfficeTrackingModal = ({ open, setOpen }: OfficeTrackingModalProps) => {
  const dispatch = useDispatch()
  const {
    loadingTracking,
    officeFilters,
    sharesOffice: { offices },
    sharesTracking,
    trackingPagination,
  } = useSelector((state) => state.fairShare)
  const { currentPage, pageSize, totalItems } = trackingPagination

  const defaultValues = {
    beginDate: dateSubtract(
      getToday(),
      15,
      'days',
      'YYYY-MM-DDTHH:mm:ss',
      'YYYY-MM-DDTHH:mm:ssZ[Z]',
    ),
    endDate: formatDate(getToday(), 'YYYY-MM-DDTHH:mm:ss'),
  }

  const {
    control,
    trigger,
    getValues,
    setValue,
    formState: { isValid },
  } = useForm({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(fairShareTrackingModalSchema),
  })

  const { getBMUs, getRegionals, getZones } = useCommercialStructure()

  const bmus = getBMUs()
  const regionals = getRegionals()
  const zones = getZones()

  const getBmuOption = () => {
    if (officeFilters?.businessUnit) {
      const bmu = bmus.find((bmu) => bmu.bmuCode === officeFilters?.businessUnit)
      if (bmu) {
        return [{ title: bmu.bmuName, value: bmu.bmuCode }]
      }
    }
    return []
  }

  const getRegionalOption = () => {
    if (officeFilters?.teamCode) {
      const regional = regionals.find((regional) => regional.id === officeFilters?.teamCode)
      if (regional) {
        return [{ title: `${regional.id} - ${regional.description}`, value: regional.id }]
      }
    }
    return [{ title: t('common.all', { context: 'female' }), value: '' }]
  }

  const getZoneOption = () => {
    if (officeFilters?.regionCode) {
      const zone = zones.find((zone) => zone.id === officeFilters?.regionCode)
      if (zone) {
        return [{ title: `${zone.id} - ${zone.description}`, value: zone.id }]
      }
    }
    return [{ title: t('common.all', { context: 'female' }), value: '' }]
  }

  const getDefaultOffice = () => {
    if (officeFilters?.officeCode) {
      return officeFilters?.officeCode
    }
    if (offices.length > 0) {
      return offices[0].officeCode
    }
    return ''
  }

  const [selectedOfficeCode, setSelectedOfficeCode] = useState<string>(getDefaultOffice())

  const trackingOfficeTabs: ItemActionTabsType[] = offices.map((office) => ({
    label: office.officeCode,
    onClick: () => {
      setSelectedOfficeCode(office.officeCode)
      dispatch(setTrackingPage(1))
    },
  }))

  const handleClose = () => {
    setOpen(false)
  }

  const fetchTrackingData = (page = currentPage) => {
    if (!selectedOfficeCode) return
    const payload: FairShareTrackingPayload = {
      bmuCode: officeFilters?.businessUnit || null,
      centerCode: officeFilters?.centerCode || null,
      colorCode: officeFilters?.colorCodes || null,
      currentPage: page,
      endDate: getValues('endDate'),
      materialCode: officeFilters?.materialCodes || null,
      officeCode: selectedOfficeCode,
      pageSize: pageSize,
      regionCode: officeFilters?.regionCode || null,
      startDate: getValues('beginDate'),
      teamCode: officeFilters?.teamCode || null,
      yearMonth: officeFilters?.yearMonth || null,
    }
    dispatch(fetchFairShareTracking(payload))
  }

  useEffect(() => {
    if (open && selectedOfficeCode) {
      fetchTrackingData()
    }
  }, [open, selectedOfficeCode, currentPage, pageSize])

  useEffect(() => {
    return () => {
      dispatch(resetTracking())
    }
  }, [dispatch])

  const handleSearch = () => {
    if (currentPage > 1) {
      dispatch(setTrackingPage(1))
    } else {
      fetchTrackingData(1)
    }
  }

  const tableLabels: HeadItemType[] = [
    { columnName: 'date', label: t('components.trackingModal.date') },
    { columnName: 'time', label: t('components.trackingModal.time') },
    { columnName: 'user', label: t('components.trackingModal.user') },
    { columnName: 'center', label: t('common.center') },
    { columnName: 'material', label: t('common.material') },
    { columnName: 'color', label: t('common.color') },
    { columnName: 'category', label: t('components.trackingModal.category') },
    { columnName: 'action', label: t('components.trackingModal.action') },
    { columnName: 'description', label: t('components.trackingModal.description') },
  ]

  const tableData = () =>
    sharesTracking.data.map((item, index) => ({
      content: [
        item.date,
        item.time,
        item.user,
        item.centerCode,
        <Flex direction="column" id={`material-${item.materialCode}`}>
          <Text size="xs">{capitalize(item.materialDescription)}</Text>
          <Text color="main" size="xs">
            {item.materialCode}
          </Text>
        </Flex>,
        <Flex direction="column" id={`color-${item.colorCode}`}>
          <Text size="xs">{capitalize(item.colorDescription)}</Text>
          <Text color="main" size="xs">
            {item.colorCode}
          </Text>
        </Flex>,
        item.origin,
        item.event,
        item.changeDetails,
      ],
      id: `${item.officeCode}-${item.dateHour}-${index}`,
    }))

  return (
    <Modal
      closeAction={handleClose}
      modalWidth={1232}
      open={open}
      returnAction={handleClose}
      setOpen={setOpen}
      showAction={false}
      showClose
      showReturn={false}
      title={
        <Flex direction="column" gap="sm">
          <Text color="stronger" size="md" weight="md">
            {t('components.trackingModal.title')}
          </Text>
          <Text size="xs">
            <TranslateComponent
              i18nKey="components.trackingModal.trackingDescriptionMonth"
              values={{
                strong: <strong />,
              }}
            />
          </Text>
        </Flex>
      }
      titleSize={18}
    >
      <Flex direction="column" fullWidth gap="md">
        <S.GridContainer>
          {/* NOTE: Only date inputs are editable on this component */}

          <S.FieldWrapper className="grid-1-3">
            <Controller
              control={control}
              name="beginDate"
              render={({ field, fieldState }) => (
                <DatePicker
                  errorLabel={fieldState.error?.message}
                  errorState={!!fieldState.error?.message}
                  height={30}
                  label={t('components.trackingModal.begin')}
                  maxDate={getToday()}
                  minDate={dateSubtract(getToday(), 5, 'year', DATE_FORMAT, DATE_FORMAT)}
                  onChange={(value) => {
                    const date = formatDate(value, DATE_FORMAT)
                    field.onChange(date)
                    const monthDiff = getDiffBetweenDates(date, getValues('endDate'), 'month')
                    const dayDiff = getDiffBetweenDates(date, getValues('endDate'), 'day')
                    if (monthDiff !== 0 || dayDiff < 0) {
                      const endDate = dateAdd(date, 1, 'month', DATE_FORMAT, DATE_FORMAT)
                      if (notInFuture(endDate)) {
                        setValue('endDate', endDate)
                      } else {
                        setValue('endDate', getToday(DATE_FORMAT))
                      }
                    }
                    trigger(['endDate'])
                  }}
                  value={field.value}
                />
              )}
            />
          </S.FieldWrapper>

          <S.FieldWrapper className="grid-3-5">
            <Controller
              control={control}
              name="endDate"
              render={({ field, fieldState }) => (
                <DatePicker
                  errorLabel={fieldState.error?.message}
                  errorState={!!fieldState.error?.message}
                  height={30}
                  label={t('components.trackingModal.end')}
                  maxDate={getToday()}
                  minDate={dateSubtract(getToday(), 5, 'year', DATE_FORMAT, DATE_FORMAT)}
                  onChange={(value) => {
                    const date = formatDate(value, DATE_FORMAT)
                    field.onChange(date)
                    const monthDiff = getDiffBetweenDates(getValues('beginDate'), date, 'month')
                    const dayDiff = getDiffBetweenDates(getValues('beginDate'), date, 'day')
                    if (monthDiff !== 0 || dayDiff < 0) {
                      const beginDate = dateSubtract(date, 1, 'month', DATE_FORMAT, DATE_FORMAT)
                      setValue('beginDate', beginDate)
                    }
                    trigger(['beginDate'])
                  }}
                  value={field.value}
                />
              )}
            />
          </S.FieldWrapper>

          {/* NOTE: Read-only inputs, permanently disabled */}

          <S.FieldWrapper className="grid-5-7">
            <Select
              disabled
              id="tracking-bmu"
              label={t('common.bmu')}
              name="tracking-bmu"
              onChange={() => undefined}
              options={getBmuOption()}
              value={officeFilters?.businessUnit ?? ''}
            />
          </S.FieldWrapper>

          <S.FieldWrapper className="grid-7-9">
            <Select
              disabled
              id="tracking-center"
              label={t('common.center')}
              name="tracking-center"
              onChange={() => undefined}
              options={
                officeFilters?.centerCode
                  ? [{ title: officeFilters.centerCode, value: officeFilters.centerCode }]
                  : [{ title: t('common.all', { context: 'male' }), value: '' }]
              }
              value={officeFilters?.centerCode ?? ''}
            />
          </S.FieldWrapper>

          <S.FieldWrapper className="grid-9-11">
            <Select
              disabled
              id="tracking-regional"
              label={t('common.regional')}
              name="tracking-regional"
              onChange={() => undefined}
              options={getRegionalOption()}
              value={officeFilters?.teamCode ?? ''}
            />
          </S.FieldWrapper>

          <S.FieldWrapper className="grid-11-13">
            <Select
              disabled
              id="tracking-zone"
              label={t('common.zone')}
              name="tracking-zone"
              onChange={() => undefined}
              options={getZoneOption()}
              value={officeFilters?.regionCode ?? ''}
            />
          </S.FieldWrapper>

          <S.FieldWrapper className="grid-1-7">
            <BatchSearch
              disabled
              label={t('common.material')}
              onChange={() => undefined}
              onClear={() => undefined}
              placeHolder={t('common.all', { context: 'male' })}
              value={officeFilters?.materialCodes?.join(',') ?? ''}
            />
          </S.FieldWrapper>

          <S.FieldWrapper className="grid-7-12">
            <BatchSearch
              disabled
              label={t('common.color')}
              onChange={() => undefined}
              onClear={() => undefined}
              placeHolder={t('common.all', { context: 'female' })}
              value={officeFilters?.colorCodes?.join(',') ?? ''}
            />
          </S.FieldWrapper>

          <S.FieldWrapper className="grid-12-13">
            <SlimButton
              content={t('buttons.search')}
              disabled={!isValid || loadingTracking}
              onClick={handleSearch}
              type="button"
              variant="primary"
            />
          </S.FieldWrapper>
        </S.GridContainer>

        <S.TabsWrapper>
          <ActionTabs
            currentItem={String(
              offices.findIndex((office) => office.officeCode === selectedOfficeCode),
            )}
            items={trackingOfficeTabs}
          />
        </S.TabsWrapper>

        {loadingTracking ? (
          <LoadingHavaianas />
        ) : (
          <>
            <S.TableWrapper>
              {tableData().length ? (
                <Table
                  bodyData={tableData()}
                  checkboxSize="small"
                  distributeWidth={true}
                  headData={tableLabels}
                  maxHeight={450}
                  rowHeight={56}
                />
              ) : (
                <NotFound
                  description={t('components.trackingModal.noChangesOnPeriod')}
                  title={t('components.trackingModal.noChanges')}
                />
              )}
            </S.TableWrapper>
            <PaginationV2
              currentPage={currentPage}
              itemsPerPage={pageSize}
              itemsPerPageOptions={[10, 20, 30, 40, 50]}
              onItemsPerPageChange={(size) => dispatch(setTrackingPageSize(size))}
              onPageChange={(page) => dispatch(setTrackingPage(page))}
              totalItems={totalItems}
            />
          </>
        )}
      </Flex>
    </Modal>
  )
}
